import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import styles from './Avis.module.css';

interface AvisAdmin {
  id: number;
  productId: number;
  produitNom: string;
  rating: number;
  comment: string;
  createdAt: string;
  approved: boolean;
  auteur: string;
}

const Stars: React.FC<{ rating: number }> = ({ rating }) => (
  <span className={styles.stars}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span key={i} className={i <= rating ? styles.starOn : styles.starOff}>★</span>
    ))}
  </span>
);

export const Avis: React.FC = () => {
  const [avis, setAvis] = useState<AvisAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'tous' | 'attente' | 'approuves'>('tous');

  useEffect(() => { loadAvis(); }, []);

  const loadAvis = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get<{ data: AvisAdmin[] }>('avisproducts/admin');
      setAvis(res.data.data ?? []);
    } catch (err: any) {
      setError('Erreur chargement : ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await api.put(`avisproducts/${id}/approve`);
      setAvis(prev => prev.map(a => a.id === id ? { ...a, approved: true } : a));
    } catch (err: any) {
      alert('Erreur : ' + (err.response?.data?.message || err.message));
    }
  };

  const handleReject = async (id: number) => {
    try {
      await api.put(`avisproducts/${id}/reject`);
      setAvis(prev => prev.map(a => a.id === id ? { ...a, approved: false } : a));
    } catch (err: any) {
      alert('Erreur : ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer cet avis définitivement ?')) return;
    try {
      await api.delete(`avisproducts/${id}`);
      setAvis(prev => prev.filter(a => a.id !== id));
    } catch (err: any) {
      alert('Erreur : ' + (err.response?.data?.message || err.message));
    }
  };

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const filtered = avis.filter(a => {
    if (filter === 'attente') return !a.approved;
    if (filter === 'approuves') return a.approved;
    return true;
  });

  const nbAttente = avis.filter(a => !a.approved).length;

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <h1 className={styles.title}>
            Avis produits
            {nbAttente > 0 && <span className={styles.badge}>{nbAttente} en attente</span>}
          </h1>
          <button onClick={loadAvis} className={styles.btnRefresh} disabled={loading}>
            {loading ? 'Chargement...' : '↻ Actualiser'}
          </button>
        </div>

        <div className={styles.filterBar}>
          {(['tous', 'attente', 'approuves'] as const).map(f => (
            <button
              key={f}
              className={`${styles.filterBtn} ${filter === f ? styles.filterBtnActive : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'tous' ? `Tous (${avis.length})` : f === 'attente' ? `En attente (${nbAttente})` : `Approuvés (${avis.length - nbAttente})`}
            </button>
          ))}
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {!loading && filtered.length === 0 ? (
          <div className={styles.noData}>Aucun avis trouvé.</div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Auteur</th>
                  <th>Note</th>
                  <th>Commentaire</th>
                  <th>Date</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => (
                  <tr key={a.id} className={styles.row}>
                    <td className={styles.colProduit}>{a.produitNom}</td>
                    <td className={styles.colAuteur}>{a.auteur}</td>
                    <td><Stars rating={a.rating} /></td>
                    <td className={styles.colComment}>{a.comment}</td>
                    <td className={styles.colDate}>{fmtDate(a.createdAt)}</td>
                    <td>
                      {a.approved
                        ? <span className={`${styles.statut} ${styles.statutApprouve}`}>✓ Approuvé</span>
                        : <span className={`${styles.statut} ${styles.statutAttente}`}>⏳ En attente</span>
                      }
                    </td>
                    <td>
                      <div className={styles.actions}>
                        {!a.approved && (
                          <button className={styles.btnApprove} onClick={() => handleApprove(a.id)}>
                            ✓ Approuver
                          </button>
                        )}
                        {a.approved && (
                          <button className={styles.btnReject} onClick={() => handleReject(a.id)}>
                            ✕ Refuser
                          </button>
                        )}
                        <button className={styles.btnDelete} onClick={() => handleDelete(a.id)}>
                          🗑
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};
